export type PreferredChannel = "email" | "telegram" | "whatsapp";

export type TeamPerson = {
  id: string;
  name: string;
  phone: string;
  email: string;
  active: boolean;
  created_at: string;
};

export type TeamMemberRow = TeamPerson & {
  role: string;
  hourly_rate: number;
  preferred_channel: PreferredChannel;
  /** Altbestand — wurde zuletzt von der Base44-App geschrieben, nicht mehr gepflegt. */
  team_app_last_seen_at: string;
  /** Abgeleitet aus vg_user/vg_session, nicht gemerkt. */
  app_status?: "kein_zugang" | "eingeladen" | "aktiv";
  app_zuletzt?: string;
  iban: string;
  bic: string;
  kontoinhaber: string;
  verdient: number;
  ausgezahlt: number;
  offener_saldo: number;
  verdient_jahr: number;
  letzte_auszahlung: string;
  einladungen_offen: number;
  einsaetze_zugesagt: number;
  bewerbungen_offen: number;
  einsaetze_abgesagt: number;
  arbeitszeiten_offen: number;
};

export type TeamOwner = TeamPerson;

export type TeamDashboard = {
  members: TeamMemberRow[];
  owners: TeamOwner[];
  summary: {
    active_member_count: number;
    total_saldo_offen_active: number;
    members_with_open_balance: number;
    earned_current_year: number;
    pending_invitation_count: number;
    accepted_upcoming_count: number;
    pending_application_count: number;
    declined_upcoming_count: number;
    open_work_entry_count: number;
  };
};

export type TeamMemberProfile = Omit<
  TeamMemberRow,
  "verdient" | "ausgezahlt" | "offener_saldo" | "verdient_jahr" | "letzte_auszahlung"
>;

export type TeamPayroll = {
  verdient_gesamt: number;
  belegt: number;
  ausgezahlt_gesamt: number;
  saldo_offen: number;
  rechnung_fehlt: number;
  unberechnet: number;
  verdient_jahr: number;
  letzte_auszahlung: string;
};

export type TeamWorkEntry = {
  id: string;
  termin_id: string | null;
  projekt_id: string;
  projekt_title: string;
  start_at: string;
  end_at: string;
  work_date: string;
  pause_stunden: number;
  hours: number;
  stundenlohn: number;
  zusatzkosten: number;
  verguetungsart: string;
  pauschale_id: string | null;
  note: string;
  confirm_status: string;
  erfasst_von: string;
  ablehnung_grund: string;
};

export type TeamFlatRate = {
  id: string;
  projekt_id: string;
  projekt_title: string;
  bezeichnung: string;
  betrag: number;
  datum: string;
  /** Gesetzt = als verdient freigegeben; vorher schuldet die Firma den Betrag noch nicht. */
  freigegeben_am?: string | null;
};

export type TeamAssignment = {
  id: string;
  termin_id: string | null;
  projekt_id: string;
  projekt_title: string;
  start_at: string;
  end_at: string;
  note: string;
  einladungs_status: string;
};

export type TeamPayout = {
  id: string;
  member_id: string;
  betrag: number;
  zahlungsdatum: string;
  vermerk: string;
  /** Wer gezahlt hat. "bar" ist ein Altwert (eine Zeile) und heißt wie inhaber + bar. */
  zahlungsweg: "gbr_konto" | "inhaber" | "bar";
  /** Wie gezahlt wurde. Bar ist immer privat — die GbR hat keine Kasse. */
  zahlungsart: Zahlungsart;
  inhaber_id: string | null;
  inhaber_name: string;
  erfasst_von_name: string;
  beleg_id: string | null;
  kontobewegung_id: string | null;
  beleg_lieferant: string;
  beleg_notiz: string;
  created_at: string;
  /** Zahlungsnachweis für den Steuerberater — nur bei privater Zahlung. */
  nachweis_nummer: string;
  nachweis_status: NachweisStatus;
  nachweis_versendet_am: string;
};

export type Zahlungsart = "ueberweisung" | "bar";

/**
 * "" = braucht keinen (GbR-Konto) · fehlt · versendet · nicht_versendet ·
 * veraltet (PDF passt nicht mehr zur Zahlung) · ungueltig (Zahlung lief
 * inzwischen übers GbR-Konto, der Nachweis war aber schon raus).
 */
export type NachweisStatus = "" | "fehlt" | "versendet" | "nicht_versendet" | "veraltet" | "ungueltig";

export type TeamMemberDetail = {
  member: TeamMemberProfile;
  year: number;
  payroll: TeamPayroll;
  entries: TeamWorkEntry[];
  assignments: TeamAssignment[];
  pauschalen: TeamFlatRate[];
  payouts: TeamPayout[];
};

export type TeamMemberPatch = Partial<Pick<
  TeamMemberProfile,
  "name" | "phone" | "email" | "role" | "active" | "hourly_rate" |
  "preferred_channel" | "iban" | "bic" | "kontoinhaber"
>>;

export type TeamOwnerPatch = Partial<Pick<TeamOwner, "name" | "phone" | "email" | "active">>;

export type PayoutCreate = {
  betrag: number;
  zahlungsdatum: string;
  vermerk: string;
  zahlungsweg: "gbr_konto" | "inhaber";
  zahlungsart: Zahlungsart;
  inhaber_id: string;
  /** Privat gezahlt: Zahlungsnachweis gleich an DATEV schicken (Vorgabe ja). */
  nachweis?: boolean;
};

export const euro = (value: number, digits = 2) => value.toLocaleString("de-DE", {
  style: "currency",
  currency: "EUR",
  minimumFractionDigits: digits,
  maximumFractionDigits: digits,
});

export const shortDate = (value?: string) => {
  if (!value) return "—";
  const parsed = new Date(value.length === 10 ? `${value}T12:00:00` : value);
  return Number.isNaN(parsed.valueOf()) ? value : parsed.toLocaleDateString("de-DE");
};

export const timeLabel = (value: string) => new Date(value).toLocaleTimeString("de-DE", {
  hour: "2-digit",
  minute: "2-digit",
});

export const monthLabel = (value: string) => new Date(`${value.slice(0, 7)}-15T12:00:00`)
  .toLocaleDateString("de-DE", { month: "long", year: "numeric" });

/** Ein Inhaber-Einsatz (termin_slot mit person_typ='inhaber'). */
export type OwnerEinsatz = {
  slot_id: string;
  datum: string;
  projekt_id: string;
  projekt_title: string;
  start_at: string | null;
  end_at: string | null;
  pause_stunden: number;
  stunden: number;
  note: string;
  confirm_status: string;
};

export type OwnerProjektZeile = {
  projekt_id: string;
  projekt_title: string;
  stunden: number;
  einsaetze: number;
};

/** Antwort von GET /team/owner/{id}/detail — Kopf, Kennzahlen und Einsätze in einem. */
export type OwnerDetail = {
  id: string;
  name: string;
  phone: string;
  email: string;
  active: boolean;
  jahr: number;
  stunden_jahr: number;
  stunden_gesamt: number;
  einsaetze_jahr: number;
  projekte_jahr: number;
  einsaetze: OwnerEinsatz[];
  projekte: OwnerProjektZeile[];
};

/** Body für PATCH /slots/{id} aus der Team-Sicht. */
export type WorkEntryPatch = Partial<{
  start_at: string;
  end_at: string;
  pause_stunden: number;
  stundenlohn: number;
  zusatzkosten: number;
  note: string;
}>;
